import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Pill, FileText, Plus, Printer, ArrowRight } from "lucide-react";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-5 border-slate-200" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>{value}</div>
          <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState({ total_patients: 0, total_medicines: 0, prescriptions_today: 0, recent_prescriptions: [], most_prescribed: [] });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const reprintLast = async () => {
    try {
      const { data: last } = await api.get("/prescriptions/last");
      nav(`/prescriptions/${last.id}/preview`);
    } catch {
      // no prescriptions yet — go to new
      nav("/prescriptions/new");
    }
  };

  const recent = Array.isArray(data?.recent_prescriptions) ? data.recent_prescriptions : [];
  const most = Array.isArray(data?.most_prescribed) ? data.most_prescribed : [];

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Overview of clinic prescription activity</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={reprintLast} variant="outline" data-testid="btn-reprint-last">
            <Printer size={14} className="mr-2" /> Reprint Last
          </Button>
          <Button onClick={() => nav("/prescriptions/new")} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-new-prescription">
            <Plus size={14} className="mr-2" /> New Prescription
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Stat icon={Users} label="Total Patients" value={data.total_patients ?? 0} testid="stat-patients" />
        <Stat icon={Pill} label="Active Medicines" value={data.total_medicines ?? 0} testid="stat-medicines" />
        <Stat icon={FileText} label="Today's Prescriptions" value={data.prescriptions_today ?? 0} testid="stat-today" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Recent Prescriptions</h2>
            <Link to="/prescriptions" className="text-sm text-blue-700 hover:underline flex items-center gap-1" data-testid="link-all-prescriptions">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No prescriptions yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recent.map((r) => (
                <Link
                  key={r.id}
                  to={`/prescriptions/${r.id}/preview`}
                  className="flex items-center justify-between py-3 hover:bg-slate-50 -mx-2 px-2 rounded-md"
                  data-testid={`recent-rx-${r.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{r?.patient_snapshot?.full_name || "—"}</div>
                    <div className="text-xs text-slate-500">{r?.prescription_number} · {(r?.medicines || []).length} med(s)</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                    r.status === "printed" || r.status === "reprinted"
                      ? "bg-green-50 text-green-700"
                      : r.status === "saved"
                      ? "bg-blue-50 text-blue-700"
                      : "bg-slate-100 text-slate-600"
                  }`}>{r.status}</span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 border-slate-200">
          <h2 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: "Work Sans" }}>Most Prescribed</h2>
          {most.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No data yet.</div>
          ) : (
            <ul className="space-y-2">
              {most.map((m, i) => (
                <li key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 truncate">{m.medicine_name}</span>
                  <span className="text-slate-500 mono text-xs">{m.count}×</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
